<script setup lang="ts">
import { Form, router } from "@inertiajs/vue3"
import { ref, watch } from "vue"

import FormField from "@/components/custom/FormField.vue"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import {
  approveManagerInsurancePath,
  rejectManagerInsurancePath,
} from "@/routes"
import type { ManagerInsurance } from "@/types/serializers"

const props = defineProps<{
  data: ManagerInsurance
}>()

const open = ref(false)
const showRejectForm = ref(false)
const rejectReason = ref("")

function closeDialog() {
  showRejectForm.value = false
  rejectReason.value = ""
  open.value = false
}

function approve() {
  router.visit(approveManagerInsurancePath(props.data.id), {
    method: "post",
    onSuccess: () => {
      closeDialog()
    },
  })
}

function openRejectForm() {
  showRejectForm.value = true
}

function cancelReject() {
  showRejectForm.value = false
  rejectReason.value = ""
}

watch(open, (value) => {
  if (!value) cancelReject()
})
</script>

<template>
  <Dialog v-model:open="open">
    <DialogTrigger>
      <Button variant="link" class="h-4 px-0">Abrir</Button>
    </DialogTrigger>
    <DialogContent>
      <DialogHeader>
        <DialogTitle :class="cn('font-bold')">
          Solicitud ID: {{ data.id }} (F)
        </DialogTitle>
        <DialogDescription> Revisión de seguro </DialogDescription>
      </DialogHeader>
      <div class="flex flex-col gap-2">
        <Card>
          <CardContent>
            <p class="mb-4 font-bold">Información del Empleado</p>

            <div class="grid grid-cols-2">
              <div class="flex flex-col gap-0.5">
                <p class="text-muted-foreground text-xs">Empleado:</p>
                <p>{{ data.user.full_name }}</p>
              </div>

              <div class="flex flex-col gap-0.5">
                <p class="text-muted-foreground text-xs">Departamento:</p>
                <p>{{ data.user.department.toUpperCase() }}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <template v-if="data.type === 'InsuranceExtActivate'">
          <Card>
            <CardContent>
              <p class="mb-4 font-bold">ID-card front side</p>

              <a
                v-if="data.id_card_front"
                :href="data.id_card_front"
                target="_blank"
                download
                class="text-primary underline"
              >
                Ver Comprobante
              </a>
              <span v-else>No hay comprobante disponible</span>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <p class="mb-4 font-bold">ID-card back side</p>

              <a
                v-if="data.id_card_back"
                :href="data.id_card_back"
                target="_blank"
                download
                class="text-primary underline"
              >
                Ver Comprobante
              </a>
              <span v-else>No hay comprobante disponible</span>
            </CardContent>
          </Card>
        </template>
      </div>
      <DialogFooter v-if="data.status === 'pending'">
        <Form
          v-if="showRejectForm"
          :action="rejectManagerInsurancePath(data.id)"
          method="post"
          class="w-full space-y-3"
          disable-while-processing
          :options="{ preserveScroll: true, preserveState: true }"
          @success="closeDialog"
          #default="{ processing, errors }"
        >
          <FormField label="Motivo de rechazo" :errors="errors.reject_reason">
            <Textarea
              v-model="rejectReason"
              name="reject_reason"
              rows="3"
              placeholder="Escribe el motivo del rechazo"
              :disabled="processing"
            />
          </FormField>
          <div class="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              :disabled="processing"
              @click="cancelReject"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="destructive"
              :disabled="processing || !rejectReason.trim()"
            >
              Rechazar
            </Button>
          </div>
        </Form>

        <template v-else>
          <Button variant="destructive" size="lg" @click="openRejectForm">
            Rechazar
          </Button>
          <Button size="lg" @click="approve">Aprobar</Button>
        </template>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
